import { useMemo } from "react";
import styled from "styled-components";
import { Label } from "@sablier/v2-components/molecules";
import { useT } from "@sablier/v2-locales";
import {
  Card as Base,
  Characteristic,
  Warning,
} from "~/client/components/molecules";
import { useWithdrawMultipleField } from "~/client/hooks";
import { useCoverWithdrawMultiple } from "~/client/hooks/covers";
import { useFormPrepare } from "../logic";

const WrapperPartial = styled(Base)`
  width: 100%;
  *[data-component="card"] {
    border-radius: 8px;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    position: relative;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;

    *[data-component="label"] {
      color: ${(props) => props.theme.colors.gray100};
    }
  }
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    position: relative;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    & > * {
      width: 100%;
    }
  }
`;

const Explanation = styled.div`
  width: 100%;
  & > p {
    ${(props) => props.theme.styles.textParagraph}
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Nope = styled.div`
  & > div {
    background-color: ${(props) => props.theme.colors.dark100};
  }
  * {
    color: ${(props) => props.theme.colors.yellow};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxSM} {
    gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    ${Explanation} {
      & > p {
        line-height: 20pt;
      }
    }
    ${Nope} {
      & > div p {
        line-height: 20pt;
      }
    }
  }
`;

function Summary() {
  const { data } = useCoverWithdrawMultiple();
  const amounts = useWithdrawMultipleField("amounts");
  const { error } = useFormPrepare();
  const streams = useMemo(() => data.streams, [data]);
  const { t } = useT();

  return (
    <Wrapper isTopHidden>
      <Content>
        <Label value={t("modalCovers.withdrawMultiple.summary")} />

        <Explanation>
          <p>{t("modalCovers.withdrawMultiple.description")}</p>
        </Explanation>
        {error && (
          <>
            <Nope>
              <Warning.General
                label={{
                  value: t("modalCovers.withdrawMultiple.warningTitle"),
                }}
                description={t(
                  "modalCovers.withdrawMultiple.warningDescription",
                  {
                    error,
                  },
                )}
              />
            </Nope>

            <Divider />
          </>
        )}
        <List>
          {streams.map((stream) => (
            <Characteristic.Instance
              key={stream.id}
              stream={stream}
              purpose={"withdraw"}
              warning={error}
              value={
                amounts.field.value?.find((a) => a.id === stream.id)?.amount
              }
            />
          ))}
        </List>
      </Content>
    </Wrapper>
  );
}

export default Summary;
